import type { ReactNode } from "react";
import { Route } from "react-router";
import { lazyNamed } from "../routes/lazyNamed";
import { RequireAdmin } from "../settings/users/RequireAdmin";
import { ExportFromParams } from "./common/ExportFromParams";
import { DefaultReportsClientProvider } from "./data/DefaultReportsClientProvider";
import { ReportsIndex } from "./ReportsIndex";

// Quality pass: each report page is its own chunk, loaded when opened.
const ActivityReportPage = lazyNamed(
  () => import("./activity/ActivityReportPage"),
  "ActivityReportPage",
);
const CallHoursReportPage = lazyNamed(
  () => import("./callHours/CallHoursReportPage"),
  "CallHoursReportPage",
);
const CallOutcomesReportPage = lazyNamed(
  () => import("./callOutcomes/CallOutcomesReportPage"),
  "CallOutcomesReportPage",
);
const DataQualityReportPage = lazyNamed(
  () => import("./dataQuality/DataQualityReportPage"),
  "DataQualityReportPage",
);
const DealSourcesReportPage = lazyNamed(
  () => import("./dealSources/DealSourcesReportPage"),
  "DealSourcesReportPage",
);
const FunnelReportPage = lazyNamed(
  () => import("./funnel/FunnelReportPage"),
  "FunnelReportPage",
);
const LossReasonsReportPage = lazyNamed(
  () => import("./lossReasons/LossReasonsReportPage"),
  "LossReasonsReportPage",
);
// GAVION rep stats
const RepStatsPage = lazyNamed(
  () => import("./repStats/RepStatsPage"),
  "RepStatsPage",
);
const CostsReportPage = lazyNamed(
  () => import("./costs/CostsReportPage"),
  "CostsReportPage",
);
// GAVION team sessions
const SessionsReportPage = lazyNamed(
  () => import("./sessions/SessionsReportPage"),
  "SessionsReportPage",
);
const SessionDetailPage = lazyNamed(
  () => import("./sessions/SessionDetailPage"),
  "SessionDetailPage",
);
const RankingReportPage = lazyNamed(
  () => import("./ranking/RankingReportPage"),
  "RankingReportPage",
);
// GAVION commissions
const CommissionsPage = lazyNamed(
  () => import("../commissions/reports/CommissionsPage"),
  "CommissionsPage",
);
const StatementPage = lazyNamed(
  () => import("../commissions/reports/StatementPage"),
  "StatementPage",
);
const MyCommissionsPage = lazyNamed(
  () => import("../commissions/reports/MyCommissionsPage"),
  "MyCommissionsPage",
);

const page = (el: ReactNode) => (
  <DefaultReportsClientProvider>{el}</DefaultReportsClientProvider>
);

/** Spread inside <CustomRoutes> (ra-core flattens arrays of <Route>). */
export const gavionReportRoutes = () => [
  <Route key="rapports" path="/rapports" element={page(<ReportsIndex />)} />,
  <Route
    key="entonnoir"
    path="/rapports/entonnoir"
    element={page(
      <FunnelReportPage actions={<ExportFromParams table="funnel_deals" />} />,
    )}
  />,
  <Route
    key="activite"
    path="/rapports/activite"
    element={page(<ActivityReportPage />)}
  />,
  // GAVION X1
  <Route
    key="classement"
    path="/rapports/classement"
    element={page(<RankingReportPage />)}
  />,
  // GAVION rep stats: a rep's detail (admin, or the rep themself; re-checked in SQL) and Coûts & CAC (admins)
  <Route
    key="vendeur"
    path="/rapports/vendeurs/:salesId"
    element={page(<RepStatsPage />)}
  />,
  <Route
    key="couts"
    path="/rapports/couts"
    element={page(
      <RequireAdmin>
        <CostsReportPage />
      </RequireAdmin>,
    )}
  />,
  // GAVION team sessions: admins only (RequireAdmin here, is_admin() in the three RPCs)
  <Route
    key="sessions"
    path="/rapports/sessions"
    element={page(
      <RequireAdmin>
        <SessionsReportPage />
      </RequireAdmin>,
    )}
  />,
  <Route
    key="session"
    path="/rapports/sessions/:sessionId"
    element={page(
      <RequireAdmin>
        <SessionDetailPage />
      </RequireAdmin>,
    )}
  />,
  // GAVION commissions: Commissions (admins), a statement (admin or its person: the RPC decides), Mes commissions
  <Route
    key="commissions"
    path="/rapports/commissions"
    element={page(
      <RequireAdmin>
        <CommissionsPage />
      </RequireAdmin>,
    )}
  />,
  <Route
    key="releve"
    path="/rapports/commissions/releves/:statementId"
    element={page(<StatementPage />)}
  />,
  <Route
    key="mes-commissions"
    path="/rapports/mes-commissions"
    element={page(<MyCommissionsPage />)}
  />,
  <Route
    key="appels"
    path="/rapports/resultats-appels"
    element={page(<CallOutcomesReportPage />)}
  />,
  // GAVION X5
  <Route
    key="heures"
    path="/rapports/heures-d-appel"
    element={page(<CallHoursReportPage />)}
  />,
  <Route
    key="sources"
    path="/rapports/sources"
    element={page(<DealSourcesReportPage />)}
  />,
  <Route
    key="pertes"
    path="/rapports/raisons-de-perte"
    element={page(<LossReasonsReportPage />)}
  />,
  <Route
    key="qualite"
    path="/rapports/qualite-des-donnees"
    element={page(<DataQualityReportPage />)}
  />,
];
